import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Spin } from 'antd';
import { Waypoint } from 'react-waypoint';
import i18next from 'i18next';
import { CheckOutlined } from '@ant-design/icons';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { readMessage as readMessageAction } from 'redux/enquiries/actions';
import {
  enquiriesSelector,
  getCurrentEnquiry,
} from 'redux/enquiries/selectors';

import { ENQUIRIES_STATUS_CONST } from 'configs/localData';

import { RefreshIcon } from 'components/common/SVGIcon';
import EmptyData from 'components/common/EmptyData';

import { EnquiriesResourceFilter, ListMessageItem } from 'types/enquiries';
import MessageItem from './MessageItem';
import { MessageBodyStyles } from './styles';
import ExceededInfo from './ExceededInfo';

interface Response {
  payload?: {
    data?: {
      results?: ListMessageItem[];
    };
  };
}

interface Props {
  listMessages?: ListMessageItem[];
  retrieveList?: (data?: object, isRefresh?: boolean) => Promise<Response>;
  resourceData?: EnquiriesResourceFilter;
  isShowAvatar?: boolean;
  isBlur?: boolean;
}

const MessageBody = ({
  listMessages,
  retrieveList,
  resourceData,
  isShowAvatar,
  isBlur,
}: Props) => {
  const dispatch = useDispatch();

  const { tab } = useParams();
  const containerRef = useRef(null);

  const inboxCurrentData = useSelector(getCurrentEnquiry) || {};

  const { id: inboxId, centreHasNewMessage } = inboxCurrentData;

  const lengthMessages = listMessages?.length;

  const loading = useSelector(enquiriesSelector('getMessagesLoading'));

  const [isScrollDowned, setIsScrollDowned] = useState(false);

  const isSeen =
    listMessages?.[0]?.isRead &&
    inboxCurrentData?.centreId === listMessages?.[0]?.senderId;

  const enabledLoadMore =
    !loading && lengthMessages > 0 && lengthMessages < resourceData?.total;

  const scrollToBottom = useCallback(() => {
    const msgEnd = document.getElementById('message-end-div');
    msgEnd &&
      document
        .getElementById('message-body')
        .scrollTo({ top: msgEnd.offsetTop });
  }, []);

  const handleEnterWaypoint = useCallback(() => {
    if (isScrollDowned) {
      retrieveList({}, false).then(({ payload: { data } }: Response) => {
        const msgEnd = document.getElementById(data?.results?.[0]?.id);

        document
          .getElementById('message-body')
          .scrollTo({ top: msgEnd.offsetTop - 100 });
      });
    }
  }, [retrieveList, isScrollDowned]);

  const handleReloadNewMessage = useCallback(() => {
    retrieveList({}, true).then(() => {
      scrollToBottom();
    });
  }, [retrieveList, scrollToBottom]);

  useEffect(() => {
    if (lengthMessages > 0 && centreHasNewMessage && inboxId && !isBlur) {
      dispatch(
        readMessageAction({
          id: inboxId,
          isRemoveItem: tab === ENQUIRIES_STATUS_CONST.unread.key,
        }),
      );
    }
  }, [centreHasNewMessage, dispatch, inboxId, lengthMessages, tab, isBlur]);

  useEffect(() => {
    setIsScrollDowned(false);
    inboxId &&
      retrieveList({}, true).then(() => {
        scrollToBottom();
        setIsScrollDowned(true);
      });
  }, [inboxId]); // eslint-disable-line

  const containerHeight = useMemo(
    () =>
      `calc(100vh - ${
        containerRef.current?.getBoundingClientRect().top + 78 + 10
      }px)`,
    [],
  );

  return (
    <MessageBodyStyles
      id="message-body"
      ref={containerRef}
      containerHeight={containerHeight}
      style={{ height: containerHeight }}
    >
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Spin />
        </div>
      )}
      {lengthMessages > 0 && (
        <>
          {enabledLoadMore && <Waypoint onEnter={handleEnterWaypoint} />}
          <div className="messages-list" id="messages-content">
            {isSeen && (
              <div className="seen-section">
                <CheckOutlined />
                <span>{i18next.t('enquiries.seen')}</span>
              </div>
            )}
            {listMessages?.map((message) => (
              <MessageItem
                key={message.id}
                message={message}
                isShowAvatar={isShowAvatar}
              />
            ))}
            {isBlur && <ExceededInfo centreId={inboxCurrentData?.centreId} />}
          </div>
          {!loading && (
            <Button
              className="reload-new-message"
              icon={<RefreshIcon />}
              onClick={handleReloadNewMessage}
            >
              {i18next.t('enquiries.reloadNewMessage')}
            </Button>
          )}
        </>
      )}
      {!loading && lengthMessages < 1 && <EmptyData />}
      <div id="message-end-div" />
    </MessageBodyStyles>
  );
};

MessageBody.defaultProps = {
  isShowAvatar: true,
  resourceData: {},
  listMessages: [],
  retrieveList: () => null,
};

export default MessageBody;
